import { Avatar, Menu, Text } from "@mantine/core";
import { useThrottledCallback } from "@mantine/hooks";
import IconLamp from "@src/assets/icons/lamp.svg?react";
import IconChevronDown from "@src/assets/icons/lucide/chevron-down.svg?react";
import IconLayoutSidebarLeftCollapse from "@src/assets/icons/tabler/layout-sidebar-left-collapse.svg?react";
import { ROUTES } from "@src/common/constants";
import { useMenuState } from "@src/components/layouts/menu/menu-drawer";
import { openPlanModal } from "@src/components/plan/plan-modal/plan-modal";
import { useMyPlanSuspense } from "@src/components/plan/queries/fetch-plan";
import { usePlanStatus } from "@src/components/plan/subscriptions/plan-status";
import { translatePlanName } from "@src/components/plan/translations";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import config from "@src/config";
import {
	DesktopProfileBadge,
	DesktopUserMenuItems,
	useDesktopProfiles,
} from "@src/desktop";
import { PlanFamily, PlanStatus, SupportedPlan } from "@src/gql/graphql";
import { useEmailVerificationStatus } from "@src/hooks/use-email-verification-status";
import clsx from "clsx";
import {
	type ComponentPropsWithoutRef,
	type ReactNode,
	forwardRef,
	useCallback,
} from "react";
import { NavLink } from "react-router-dom";
import { useAuthenticatedUser } from "../user/user-context";

function PlanBadge() {
	const { data } = useMyPlanSuspense();

	return (
		<div
			className="inline-flex items-center px-2 py-1 text-xs leading-none
			text-text-secondary border border-border-default rounded-lg min-h-4"
		>
			{translatePlanName({
				plan: data.plan,
				planFamily: data.planFamily,
				status: data.status,
			})}
		</div>
	);
}

function BelieverManageButton() {
	return (
		<a
			href={config.enterpriseContactUrl}
			target="_blank"
			rel="noopener noreferrer"
			onClick={(e) => e.stopPropagation()}
			className="rounded-lg px-2 py-1 text-xs leading-none min-h-4 inline-flex items-center text-text-secondary border border-border-default hover:bg-surface-subtle transition-colors"
		>
			Manage Plan
		</a>
	);
}

function PlanUpgradeButton({ isCancelled }: { isCancelled: boolean }) {
	const label = isCancelled ? "Renew" : "Upgrade";

	const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		openPlanModal();
	}, []);

	return (
		<button
			type="button"
			className="border border-solid border-brand-primary rounded-lg px-2 py-1 text-xs leading-none min-h-4 inline-flex items-center text-text-inverse bg-brand-primary hover:bg-brand-primary-hover gap-1"
			onClick={handleClick}
		>
			<IconLamp className="w-3 h-3" />
			{label}
		</button>
	);
}

function UserInfo() {
	const authenticatedUser = useAuthenticatedUser();

	const { data: plan } = useMyPlanSuspense();

	const isBeliever = plan.plan === SupportedPlan.Believer;
	const isStarter = plan.planFamily === PlanFamily.Starter;
	const isNotActive = plan.status !== PlanStatus.Active;
	const isCancelled = plan.status === PlanStatus.Cancelled;

	usePlanStatus();

	return (
		<div className="flex flex-col grow gap-1.5 min-w-0">
			<Text
				size="sm"
				fw={600}
				className="overflow-hidden whitespace-nowrap text-ellipsis leading-tight"
			>
				{authenticatedUser.preferredName}
			</Text>

			<div className="flex flex-wrap grow-0 gap-1 items-center">
				<PlanBadge />
				{isBeliever && <BelieverManageButton />}
				{!isBeliever && (isStarter || isNotActive || isCancelled) && (
					<PlanUpgradeButton isCancelled={isCancelled} />
				)}
			</div>
		</div>
	);
}

const HeaderIconButton = forwardRef<
	HTMLButtonElement,
	{
		ariaLabel: string;
		children: ReactNode;
	} & Omit<ComponentPropsWithoutRef<"button">, "children" | "type">
>(function HeaderIconButton({ ariaLabel, children, className, ...rest }, ref) {
	return (
		<button
			ref={ref}
			type="button"
			aria-label={ariaLabel}
			className={clsx(
				"flex shrink-0 items-center justify-center",
				"h-7.5 w-7.5 rounded-md cursor-pointer",
				"border border-transparent bg-surface-subtle",
				"text-text-secondary hover:text-text-primary",
				"hover:bg-surface-hover-subtle hover:border-border-subtle",
				"transition-colors",
				className,
			)}
			{...rest}
		>
			{children}
		</button>
	);
});

function ProfileSwitcherButton() {
	const { isElectron, profiles, currentProfile } = useDesktopProfiles();

	if (!isElectron || profiles.length === 0) {
		return null;
	}

	return (
		<Menu shadow="md" width={240} position="bottom-start" zIndex={9999}>
			<Menu.Target>
				<HeaderIconButton ariaLabel="Switch profile">
					<IconChevronDown width={14} height={14} />
				</HeaderIconButton>
			</Menu.Target>

			<Menu.Dropdown>
				<DesktopUserMenuItems currentAvatarUrl={currentProfile?.avatarUrl} />
			</Menu.Dropdown>
		</Menu>
	);
}

export function UserButton(props: { className?: string }) {
	const authenticatedUser = useAuthenticatedUser();
	const { hasVerificationPending } = useEmailVerificationStatus();
	const { toggleDrawer } = useMenuState();
	const handleCollapsePointerDown = useThrottledCallback(toggleDrawer, 100);

	return (
		<div className="flex items-center justify-between gap-1.5 w-full min-h-10">
			<NavLink
				className={clsx(
					"flex-1 min-w-0 flex flex-row items-center",
					"text-text-primary rounded-lg border border-solid",
					"border-transparent cursor-pointer",
					"hover:bg-surface-hover-subtle",
					"transition-colors",
					props.className,
				)}
				to={ROUTES.profile}
			>
				<div className="relative shrink-0 mr-2">
					<Avatar
						src={authenticatedUser.avatarUrl}
						size="34px"
						className={clsx(
							"relative before:absolute before:inset-0",
							"before:rounded-full before:border",
							"before:border-border-subtle",
						)}
					/>
					{hasVerificationPending && (
						<span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-yellow-400 border-2 border-surface-default rounded-full" />
					)}
					<DesktopProfileBadge />
				</div>

				<UserInfo />
			</NavLink>

			<ProfileSwitcherButton />

			<Tooltip label="Close navbar" openDelay={300} withArrow>
				<HeaderIconButton
					ariaLabel="Close navbar"
					onPointerDown={handleCollapsePointerDown}
				>
					<IconLayoutSidebarLeftCollapse
						width={30}
						height={30}
						strokeWidth={1}
					/>
				</HeaderIconButton>
			</Tooltip>
		</div>
	);
}
